import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { islandFloor } from '../../shared/islandField';

/**
 * The anchor rig: one 3D anchor at the bow and, while anchored, the cable
 * (rode) that runs from the bow to the drop point.
 *
 * Budget discipline: cable + anchor share a single pre-allocated
 * `BufferGeometry` and one untextured material, so the whole rig is exactly
 * ONE draw call in every state (stowed, anchored-visible, anchored-invisible).
 * The cable rings (16 vertices) are rewritten every frame because the bow
 * rides the buoyancy springs; the anchor part (~400 vertices) is only
 * rewritten when the anchor's own pose changes (stowed: follows the bow,
 * anchored: world-fixed at the drop point).
 *
 * Visibility follows the shared island field: the anchor model is rendered
 * only where `islandFloor` says a bottom exists at the drop point (submerged
 * shoal → resting on the seabed, land → on the beach). In open water the
 * field reports no bottom, so the anchor is not rendered and the cable simply
 * leaves the bow and dips below the surface. No new depth system, no
 * hardcoded coordinates — the same `islandFloor` that feeds the terrain and
 * the server's grounding.
 */

/** Signed floor (m) that still counts as "there is a bottom here". */
const FLOOR_EPSILON = 0.01;
/** Local-space anchor model: origin at the crown ring, flukes at -y. */
const ANCHOR_SHANK = 1.7;
/** Ship-local hawse point where the stowed anchor hangs off the bow. */
const HAWSE_LOCAL = new THREE.Vector3(11.3, 0.7, 0);
const CABLE_SEGMENTS = 8;
const CABLE_RADIUS = 0.045;
const UNIT = new THREE.Vector3(1, 1, 1);

/** Build the low-poly anchor model in local space (origin at the crown ring). */
function buildAnchorGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];

  // Shank: the main shaft hanging from the ring.
  const shank = new THREE.CylinderGeometry(0.11, 0.14, ANCHOR_SHANK, 6);
  shank.translate(0, -ANCHOR_SHANK / 2, 0);
  parts.push(shank);

  // Crown ring at the top (where the cable attaches).
  const ring = new THREE.TorusGeometry(0.2, 0.05, 5, 8);
  ring.translate(0, 0.18, 0);
  parts.push(ring);

  // Stock: the crossbar the crew grabs.
  const stock = new THREE.CylinderGeometry(0.06, 0.06, 0.9, 5);
  stock.rotateX(Math.PI / 2);
  stock.translate(0, -0.35, 0);
  parts.push(stock);

  // Arms + flukes: two pointed cones sweeping up from the shank foot.
  for (const side of [-1, 1]) {
    const fluke = new THREE.ConeGeometry(0.1, 0.55, 4);
    fluke.rotateX(Math.PI / 2.6);
    fluke.translate(side * 0.34, -ANCHOR_SHANK + 0.18, 0);
    parts.push(fluke);
  }

  return mergeGeometries(parts, false) as THREE.BufferGeometry;
}

/** Authoritative anchor state as read from the shared ship. */
export interface AnchorStateLike {
  anchored: boolean;
  x: number;
  z: number;
}

export class AnchorRig {
  /** Scene-level group; world-space, because while anchored the far end of
   * the cable is world-fixed and cannot ride the ship group. */
  readonly group = new THREE.Group();

  private readonly material: THREE.MeshStandardMaterial;
  private readonly mesh: THREE.Mesh;
  private readonly geometry: THREE.BufferGeometry;
  private readonly anchorGeometry: THREE.BufferGeometry;
  private readonly anchorVertexCount: number;
  /** Cached world-space anchor positions, valid while the anchor key is stable. */
  private readonly anchorWorld = new Float32Array(0);
  private anchorWorldKey = '';

  // Scratch objects: no per-frame allocations.
  private readonly hawseWorld = new THREE.Vector3();
  private readonly ringWorld = new THREE.Vector3();
  private readonly cableAxis = new THREE.Vector3();
  private readonly radial = new THREE.Vector3();
  private readonly tangent = new THREE.Vector3();
  private readonly dirScratch = new THREE.Vector3();
  private readonly upScratch = new THREE.Vector3();
  private readonly tmpMatrix = new THREE.Matrix4();
  private readonly anchorMatrix = new THREE.Matrix4();

  private lastAnchorX = 0;
  private lastAnchorZ = 0;
  private lastHullX = 0;
  private lastHullZ = 0;
  private lastVisible = false;

  constructor() {
    this.material = new THREE.MeshStandardMaterial({
      color: 0x3a3f44,
      roughness: 0.85,
      metalness: 0.35,
    });
    this.anchorGeometry = buildAnchorGeometry();
    this.anchorVertexCount = this.anchorGeometry.getAttribute('position').count;
    this.anchorWorld = new Float32Array(this.anchorVertexCount * 3);

    // One merged geometry: cable rings first, anchor model second.
    const cableVertexCount = CABLE_SEGMENTS * 2;
    const position = new Float32Array((cableVertexCount + this.anchorVertexCount) * 3);
    const normal = new Float32Array((cableVertexCount + this.anchorVertexCount) * 3);
    const anchorIndexSource = this.anchorGeometry.getIndex();
    if (!anchorIndexSource) throw new Error('AnchorRig: merged anchor geometry lost its index');
    const index = new Uint16Array(CABLE_SEGMENTS * 6 + anchorIndexSource.count);
    // Cable: quad strip between the two rings (open, no caps).
    let offset = 0;
    for (let s = 0; s < CABLE_SEGMENTS; s += 1) {
      const a = s * 2;
      const b = ((s + 1) % CABLE_SEGMENTS) * 2;
      index[offset] = a;
      index[offset + 1] = b;
      index[offset + 2] = a + 1;
      index[offset + 3] = b;
      index[offset + 4] = a + 1;
      index[offset + 5] = b + 1;
      offset += 6;
    }
    // Anchor: the model's own index, shifted past the cable vertex range.
    for (let i = 0; i < anchorIndexSource.count; i += 1) {
      index[offset + i] = anchorIndexSource.array[i] + cableVertexCount;
    }

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(position, 3).setUsage(THREE.DynamicDrawUsage),
    );
    this.geometry.setAttribute(
      'normal',
      new THREE.BufferAttribute(normal, 3).setUsage(THREE.DynamicDrawUsage),
    );
    this.geometry.setIndex(new THREE.BufferAttribute(index, 1));
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.frustumCulled = false; // the buffer moves every frame
    this.group.add(this.mesh);
  }

  /**
   * Per-frame update. `pose` is the authoritative anchor state (drop point in
   * world space); `posePosition`/`poseQuaternion` are the ship pose.
   */
  update(
    pose: AnchorStateLike,
    posePosition: THREE.Vector3,
    poseQuaternion: THREE.Quaternion,
  ): void {
    const anchored = pose.anchored;
    const anchorX = pose.x;
    const anchorZ = pose.z;

    // Bow hawse point in world space (the cable's near end, always).
    this.tmpMatrix.compose(posePosition, poseQuaternion, UNIT);
    this.hawseWorld.copy(HAWSE_LOCAL).applyMatrix4(this.tmpMatrix);

    // Is there a bottom under the drop point (shared island field)?
    const floor = islandFloor(anchorX, anchorZ);
    const visible = floor > FLOOR_EPSILON || floor < -FLOOR_EPSILON;

    // Cable's far end: the anchor's crown ring. On a bottom the ring sits
    // `ANCHOR_SHANK` above the seabed; in open water the rope leaves the bow
    // and dips to the surface at the drop point. Stowed: collapses onto the
    // hawse point so no cable is visible.
    if (anchored) {
      this.ringWorld.set(anchorX, Math.max(0, floor) + (visible ? ANCHOR_SHANK : 0), anchorZ);
    } else {
      this.ringWorld.copy(this.hawseWorld);
    }

    const position = this.geometry.getAttribute('position') as THREE.BufferAttribute;
    const normal = this.geometry.getAttribute('normal') as THREE.BufferAttribute;
    const positionArray = position.array as Float32Array;
    const normalArray = normal.array as Float32Array;
    const cableOffset = CABLE_SEGMENTS * 2 * 3;

    // --- Cable: CABLE_SEGMENTS quads between the hawse ring and the far ring.
    this.cableAxis.copy(this.ringWorld).sub(this.hawseWorld);
    const length = Math.max(0.05, this.cableAxis.length());
    this.cableAxis.divideScalar(length);
    // Radial basis: any axis not parallel to the cable.
    this.upScratch.set(Math.abs(this.cableAxis.y) > 0.98 ? 1 : 0, Math.abs(this.cableAxis.y) > 0.98 ? 0 : 1, 0);
    this.radial.crossVectors(this.cableAxis, this.upScratch).normalize();
    this.tangent.crossVectors(this.cableAxis, this.radial).normalize();
    for (let ring = 0; ring < 2; ring += 1) {
      const cx = (ring === 0 ? this.hawseWorld : this.ringWorld).x;
      const cy = (ring === 0 ? this.hawseWorld : this.ringWorld).y;
      const cz = (ring === 0 ? this.hawseWorld : this.ringWorld).z;
      for (let s = 0; s < CABLE_SEGMENTS; s += 1) {
        const angle = (s / CABLE_SEGMENTS) * Math.PI * 2;
        this.dirScratch
          .copy(this.radial)
          .multiplyScalar(Math.cos(angle))
          .addScaledVector(this.tangent, Math.sin(angle));
        const i = (ring * CABLE_SEGMENTS + s) * 3;
        positionArray[i] = cx + this.dirScratch.x * CABLE_RADIUS;
        positionArray[i + 1] = cy + this.dirScratch.y * CABLE_RADIUS;
        positionArray[i + 2] = cz + this.dirScratch.z * CABLE_RADIUS;
        normalArray[i] = this.dirScratch.x;
        normalArray[i + 1] = this.dirScratch.y;
        normalArray[i + 2] = this.dirScratch.z;
      }
    }

    // --- Anchor: pure-translation world transform, cached while stable.
    // Stowed it follows the bow (gravity keeps it hanging straight); anchored
    // it is world-fixed at the drop point, so the key only changes on drop/raise.
    let key: string;
    let tx: number;
    let ty: number;
    let tz: number;
    if (anchored) {
      key = `a|${anchorX.toFixed(3)}|${anchorZ.toFixed(3)}|${floor.toFixed(3)}`;
      tx = anchorX;
      ty = Math.max(0, floor) + (visible ? ANCHOR_SHANK : 0);
      tz = anchorZ;
    } else {
      key = `s|${this.hawseWorld.x.toFixed(3)}|${this.hawseWorld.y.toFixed(3)}|${this.hawseWorld.z.toFixed(3)}`;
      tx = this.hawseWorld.x;
      ty = this.hawseWorld.y;
      tz = this.hawseWorld.z;
    }
    if (key !== this.anchorWorldKey) {
      this.anchorWorldKey = key;
      this.anchorMatrix.makeTranslation(tx, ty, tz);
      const anchorPosition = this.anchorGeometry.getAttribute('position').array as Float32Array;
      const m = this.anchorMatrix.elements;
      for (let v = 0; v < this.anchorVertexCount; v += 1) {
        const lx = anchorPosition[v * 3];
        const ly = anchorPosition[v * 3 + 1];
        const lz = anchorPosition[v * 3 + 2];
        this.anchorWorld[v * 3] = m[0] * lx + m[4] * ly + m[8] * lz + m[12];
        this.anchorWorld[v * 3 + 1] = m[1] * lx + m[5] * ly + m[9] * lz + m[13];
        this.anchorWorld[v * 3 + 2] = m[2] * lx + m[6] * ly + m[10] * lz + m[14];
      }
    }
    const anchorNormal = this.anchorGeometry.getAttribute('normal').array as Float32Array;
    for (let v = 0; v < this.anchorVertexCount; v += 1) {
      positionArray[cableOffset + v * 3] = this.anchorWorld[v * 3];
      positionArray[cableOffset + v * 3 + 1] = this.anchorWorld[v * 3 + 1];
      positionArray[cableOffset + v * 3 + 2] = this.anchorWorld[v * 3 + 2];
      normalArray[cableOffset + v * 3] = anchorNormal[v * 3];
      normalArray[cableOffset + v * 3 + 1] = anchorNormal[v * 3 + 1];
      normalArray[cableOffset + v * 3 + 2] = anchorNormal[v * 3 + 2];
    }

    position.needsUpdate = true;
    normal.needsUpdate = true;
    this.mesh.visible = true;

    // Diagnostics. Stowed there is no drop point, so the distance is 0 (the
    // server keeps the anchor at the origin, which would read as a bogus
    // hull-to-origin distance otherwise).
    this.lastAnchorX = anchored ? anchorX : posePosition.x;
    this.lastAnchorZ = anchored ? anchorZ : posePosition.z;
    this.lastHullX = posePosition.x;
    this.lastHullZ = posePosition.z;
    this.lastVisible = anchored && visible;
  }

  /** Whether the anchor model is rendered (a bottom exists at the drop point). */
  get visible(): boolean {
    return this.lastVisible;
  }

  /** World distance from the hull to the drop point, metres. */
  get distance(): number {
    return Math.hypot(this.lastHullX - this.lastAnchorX, this.lastHullZ - this.lastAnchorZ);
  }

  dispose(): void {
    this.group.removeFromParent();
    this.geometry.dispose();
    this.anchorGeometry.dispose();
    this.material.dispose();
  }
}
